/**
 * The un-transformed layout size (offsetWidth x offsetHeight) of an element's parent box — the
 * photo's own box inside the viewer. CSS transforms (the viewer's rotate/zoom) do not change it,
 * so the SVG viewBox stays in the photo's pixel space. Kept current with a ResizeObserver.
 */
import { useLayoutEffect, useState, type RefObject } from 'react';

export interface BoxSize {
  w: number;
  h: number;
}

export function useBoxSize(ref: RefObject<Element | null>, fallback: BoxSize = { w: 1000, h: 750 }): BoxSize {
  const [size, setSize] = useState<BoxSize>(fallback);

  useLayoutEffect(() => {
    const parent = ref.current?.parentElement as HTMLElement | null | undefined;
    if (!parent) return undefined;
    const measure = () => {
      const w = parent.offsetWidth;
      const h = parent.offsetHeight;
      // jsdom (and a not-yet-laid-out box) reports 0: keep the fallback rather than a 0 viewBox.
      if (w > 0 && h > 0) setSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}
